import {
  usePendingApprovalNotificationPreferenceQuery,
  useSetPendingApprovalNotificationPreferenceMutation,
} from '@/types/vue-apollo-helpers';
import type { PendingApprovalNotificationPreferenceQuery } from '@/types/operations';

export type ApprovalSubscriptionScope =
  PendingApprovalNotificationPreferenceQuery['pendingApprovalNotificationSubscription']['scopes'][number];

/**
 * Staff opt-in to pending course-registration approval notifications
 * (tertiary schools). The backend keeps one row per scope: school-wide
 * (no department, no programme) or narrowed to a department and/or a
 * programme. The mutation's semantics decide the API here:
 *  - omit both ids: the school-wide row;
 *  - `subscribed: false` WITHOUT ids removes EVERY row the user holds;
 *  - `subscribed: false` WITH ids removes only that scoped row.
 */
export function useApprovalNotificationSubscription() {
  const { result, loading, refetch } = usePendingApprovalNotificationPreferenceQuery();
  const { mutate, loading: saving } = useSetPendingApprovalNotificationPreferenceMutation();

  const status = computed(() => result.value?.pendingApprovalNotificationSubscription ?? null);
  const subscribed = computed(() => status.value?.subscribed ?? false);
  const scopes = computed<ApprovalSubscriptionScope[]>(() => status.value?.scopes ?? []);
  const schoolWide = computed(() => scopes.value.some((s) => s.schoolWide));
  const scopedRows = computed(() => scopes.value.filter((s) => !s.schoolWide));

  /** School-wide on; off removes every row (scoped ones included). */
  async function setSubscribed(value: boolean) {
    await mutate({ subscribed: value });
    await refetch();
  }

  async function addScope(scope: { departmentId?: string | null; programmeId?: string | null }) {
    await mutate({ subscribed: true, departmentId: scope.departmentId ?? null, programmeId: scope.programmeId ?? null });
    await refetch();
  }

  /** Removes one scoped row; the school-wide row can only go with everything else. */
  async function removeScope(scope: ApprovalSubscriptionScope) {
    if (scope.schoolWide) {
      await mutate({ subscribed: false });
    } else {
      await mutate({ subscribed: false, departmentId: scope.departmentId ?? null, programmeId: scope.programmeId ?? null });
    }
    await refetch();
  }

  return { status, subscribed, scopes, schoolWide, scopedRows, loading, saving, setSubscribed, addScope, removeScope };
}
